import { Outlet, useNavigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { SignedIn, SignedOut, UserButton, useUser, IS_DEMO_MODE } from '@/lib/auth';
import { useTenant } from '@/hooks/useTenant';
import { Badge } from '@/components/ui';
import { PACK } from '@/lib/pack';

/**
 * Layout principal post-login : header avec marque, badge pack, UserButton Clerk.
 * Outlet reçoit le tenant via context — chaque page enfant peut le récupérer
 * via `useOutletContext<{ tenant: Tenant }>()`.
 */
export function AppLayout() {
  const { isLoaded, isSignedIn } = useUser();
  const navigate = useNavigate();
  const tenantQ = useTenant();

  useEffect(() => {
    if (isLoaded && !isSignedIn && !IS_DEMO_MODE) {
      navigate('/sign-in', { replace: true });
    }
  }, [isLoaded, isSignedIn, navigate]);

  if (!isLoaded || tenantQ.isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-ducens-muted" />
      </div>
    );
  }

  if (tenantQ.isError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-ducens-warn mx-auto mb-3" />
          <h1 className="text-xl font-semibold mb-2">Compte introuvable</h1>
          <p className="text-ducens-muted text-sm mb-4">{(tenantQ.error as Error).message}</p>
          <a
            href="mailto:sebastian@ducens.io"
            className="inline-block px-4 py-2 rounded-md bg-ducens-ink text-white text-sm hover:opacity-90"
          >
            Contacter Sebastian
          </a>
        </div>
      </div>
    );
  }

  const tenant = tenantQ.data!;
  const packInfo = PACK[tenant.pack];

  return (
    <SignedIn>
      <div className="min-h-screen flex flex-col bg-ducens-bg">
        <header className="border-b border-ducens-border bg-white sticky top-0 z-30">
          <div className="px-4 lg:px-6 py-3 flex items-center justify-between max-w-6xl mx-auto w-full">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-ducens-ink flex items-center justify-center text-ducens-accent font-bold">
                D
              </div>
              <div className="hidden sm:block">
                <div className="font-semibold text-sm leading-tight">Ducens</div>
                <div className="text-xs text-ducens-muted leading-tight">
                  {tenant.company_name}
                </div>
              </div>
            </Link>

            <div className="flex items-center gap-3">
              <Badge variant={packInfo.variant}>Pack {packInfo.name}</Badge>
              {IS_DEMO_MODE && (
                <Badge variant="warn" className="hidden sm:inline-flex">
                  démo
                </Badge>
              )}
              <SignedIn>
                <UserButton />
              </SignedIn>
              <SignedOut>
                <Link to="/sign-in" className="text-sm text-ducens-muted hover:text-ducens-ink">
                  Se connecter
                </Link>
              </SignedOut>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 lg:px-6 py-6 lg:py-10 max-w-6xl mx-auto w-full">
          <Outlet context={{ tenant }} />
        </main>

        <footer className="border-t border-ducens-border bg-white">
          <div className="max-w-6xl mx-auto px-4 lg:px-6 py-4 text-xs text-ducens-muted flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1">
            <span>Ducens — Sebastian Dominguez</span>
            <a href="mailto:sebastian@ducens.io" className="underline">
              sebastian@ducens.io
            </a>
          </div>
        </footer>
      </div>
    </SignedIn>
  );
}
